import React from 'react';
import { range } from 'lodash-es';

import {
  Box,
  Checkbox,
  FieldSet,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Legend,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../models/index';
import { Field, PaymentMethodSection } from '../../../models/index';
import { PAYMENT_METHOD } from '../../../constants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_PAYMENT_METHODS = 5;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PaymentMethodSection) => void;
};

export function PaymentMethod_v3({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: PaymentMethodSection,
  });

  const errorMsg = section.hasFieldError(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)
    ? PAYMENT_METHOD.ERROR_MESSAGE.ADDRESS
    : null;

  function onRemoveAll(): void {
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function onOptionChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value, checked } = event.currentTarget;

    if (value === PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED) {
      if (checked) {
        onAddOne();
      } else {
        onRemoveAll();
      }
    }

    setSectionValues({
      [value]: checked,
    });
  }

  function onAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS]: value,
    });
  }

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(
      PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME,
      Number(index),
    );

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderIndividualPaymentMethod(index: number) {
    const nameField = section.getField(
      Field.getIndexedFieldName(PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME, index),
    );

    return (
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Label id={`electronic-payment-method-#${index + 1}`}>Service #{index + 1}</Label>
            {index > 0 && (
              <TextButton
                data-index={index}
                data-test="delete-electronic-payment-method"
                onClick={onDeleteOne}
                textStyle="body-sm-bold"
              >
                Delete
              </TextButton>
            )}
          </Box>
        }
        control={
          <Input
            aria-labelledby={`electronic-payment-method-#${index + 1}`}
            data-index={index}
            data-test="electronic-payment-method-name"
            value={nameField.enteredValue}
            onChange={setName}
          />
        }
      />
    );
  }

  const totalPaymentMethods = section.getTotalIndexes();
  if (totalPaymentMethods == null) {
    return null;
  }

  const zillowPayments = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED);
  const checkOrMoneyOrder = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED);
  const directDeposit = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED);
  const otherElectronicPayment = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED);

  return (
    <>
      <FieldSet required legend={<Legend>Which forms of payment will you accept?</Legend>}>
        <LabeledControl
          label={
            <Label>
              Zillow Payments
              <br />
              <Text css={{ color: 'text.subtle' }}>
                Tenant pays through our online service. Set up quickly using information from your lease.
              </Text>
            </Label>
          }
          control={
            <Checkbox
              value={PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED}
              checked={zillowPayments}
              onChange={onOptionChange}
            />
          }
        />
        <LabeledControl
          disabled
          label={
            <Label>
              Check or money order
              <br />
              <Text css={{ color: 'text.subtle' }}>
                Tenant sends payment to either the landlord or the property manager.
              </Text>
            </Label>
          }
          control={
            <Checkbox
              value={PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED}
              checked={checkOrMoneyOrder || true}
              onChange={onOptionChange}
              disabled
            />
          }
        />
        <LabeledControl
          label={
            <Label>
              Direct deposit
              <br />
              <Text css={{ color: 'text.subtle' }}>
                Tenant transfers funds using bank info, including ACH, if you provide them.
              </Text>
            </Label>
          }
          control={
            <Checkbox
              value={PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED}
              checked={directDeposit}
              onChange={onOptionChange}
            />
          }
        />
        <LabeledControl
          label={
            <Label>
              Other electronic payment
              <br />
              <Text css={{ color: 'text.subtle' }}>Tenant pays via third party online service of your choice.</Text>
            </Label>
          }
          control={
            <Checkbox
              value={PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED}
              checked={otherElectronicPayment}
              onChange={onOptionChange}
            />
          }
        />
      </FieldSet>
      <FormField
        required
        css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}
        label={<Label id="tenant-send-check">To whom should the tenant send check or money order?</Label>}
        control={
          <Textarea
            aria-labelledby="tenant-send-check"
            data-test="tenant-send-check"
            error={!!errorMsg}
            onChange={onAddressChange}
            value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
            css={{ resize: 'vertical' }}
            rows={3}
          />
        }
        description={<FormHelp>Recipient’s name, street, city, state, and ZIP code</FormHelp>}
      />
      {range(0, totalPaymentMethods).map((index: number) => (
        <React.Fragment key={index}>{renderIndividualPaymentMethod(index)}</React.Fragment>
      ))}
      {totalPaymentMethods > 0 &&
        (totalPaymentMethods < MAXIMUM_PAYMENT_METHODS ? (
          <TextButton icon={<IconPlusFilled />} onClick={onAddOne} data-test="add-another">
            Add another electronic payment service
          </TextButton>
        ) : (
          <Text>
            <span data-test="maximum-reached">
              You’ve added {MAXIMUM_PAYMENT_METHODS} electronic payment services, which is the maximum allowed.
            </span>
          </Text>
        ))}
    </>
  );
}

PaymentMethod_v3.description = 'Disable check/money order selection';

export default PaymentMethod_v3;
